import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { parseScan, looksLikeScanPrefix } from '../utils/scanRouter';
import { handleScan } from '../utils/scanActions';

export const MAX_GAP_MS = 40; // characters of a scan arrive faster than this; a person types slower
export const MIN_CHARS = 6;
export const IDLE_MS = 120; // a scan with no Enter at the end is complete after this much quiet

const isTextField = (el) => !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') && !el.readOnly && !el.disabled;

/** PIN pads mark their inputs with data-no-scan; while one is mounted or focused, scans are never touched. */
export const scanSuppressed = () => {
  const a = document.activeElement;
  if (a && a.closest && a.closest('[data-no-scan]')) return true;
  return !!document.querySelector('[data-no-scan]');
};

// put an input's value back without leaving React's controlled state behind
const restoreValue = (el, value) => {
  const proto = el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value').set;
  setter.call(el, value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
};

/**
 * Mounted once, inside the Router. Recognises a barcode scanner in keyboard-wedge mode: a burst of at
 * least 6 characters with <= 40 ms between them, ended by Enter or 120 ms of quiet. Only a burst that
 * parseScan recognises (ORD:, VCH:, a voucher URL) is swallowed - the typed characters are removed from
 * a focused input and the Enter never reaches a form. Every other burst, and all normal typing, is left
 * completely alone. Also exposes window.onBarcodeScanned(code, symbology) for the native bridge.
 */
export default function ScanListener() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const ctxRef = useRef({});
  ctxRef.current = { navigate, pathname };

  useEffect(() => {
    let buf = '';
    let lastAt = 0;
    let idleTimer = null;
    let target = null;
    let startValue = null;
    let swallowing = false;

    const reset = () => {
      clearTimeout(idleTimer);
      buf = '';
      lastAt = 0;
      target = null;
      startValue = null;
      swallowing = false;
    };

    const finish = (viaEnter, event) => {
      clearTimeout(idleTimer);
      const text = buf;
      const el = target;
      const before = startValue;
      const recognised = text.length >= MIN_CHARS && parseScan(text).type !== 'unknown';
      if (recognised) {
        if (el && before !== null && el.value !== before) restoreValue(el, before);
        if (viaEnter && event) {
          event.preventDefault();
          event.stopPropagation();
        }
        handleScan(text, ctxRef.current);
      }
      reset();
    };

    const onKeyDown = (e) => {
      if (e.isComposing || e.ctrlKey || e.metaKey || e.altKey) return;
      if (scanSuppressed()) {
        reset();
        return;
      }
      const now = Date.now();

      if (e.key === 'Enter') {
        if (buf.length >= MIN_CHARS) finish(true, e);
        else reset();
        return;
      }

      if (e.key && e.key.length === 1) {
        if (buf && now - lastAt > MAX_GAP_MS) reset(); // too slow: this is a person, start over
        if (!buf) {
          const a = document.activeElement;
          target = isTextField(a) ? a : null;
          startValue = target ? target.value : null;
        }
        buf += e.key;
        lastAt = now;
        // once it clearly starts like a scan, keep its remaining characters out of the field
        if (!swallowing && buf.length >= 4 && looksLikeScanPrefix(buf)) swallowing = true;
        if (swallowing) {
          e.preventDefault();
          e.stopPropagation();
        }
        clearTimeout(idleTimer);
        idleTimer = setTimeout(() => finish(false, null), IDLE_MS);
        return;
      }

      if (e.key === 'Shift' || e.key === 'CapsLock' || e.key === 'AltGraph') return; // modifiers inside a burst
      reset(); // any other key (Backspace, Tab, arrows...) is not a scan
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      clearTimeout(idleTimer);
    };
  }, []);

  // native bridge: window.onBarcodeScanned(code, symbology)
  useEffect(() => {
    window.onBarcodeScanned = (code) => {
      if (typeof code !== 'string') return;
      handleScan(code, ctxRef.current);
    };
    return () => {
      delete window.onBarcodeScanned;
    };
  }, []);

  return null;
}
